//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import { getItemsMetadata } from '@apis/bulkActionsApi';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import * as legacyLike2BuyApi from '@apis/like2buy/like2buyLegacyApi';
import * as like2buyModerationApi from '@apis/like2buy/like2buyModerationApi';
import * as like2buyStoreApi from '@apis/like2buy/like2buyStoreApi';
import { ILike2BuyStoreData } from '@apis/like2buy/like2buyStoreApiModel';
import {
  addLike2BuyV2Items,
  archiveLike2BuyV2Items,
  getLike2BuyV2Items,
  setPinnedLike2BuyV2Items,
} from '@apis/like2buy-v2/like2BuyV2ModerationApi';
import { Like2BuyV2ModerationStatus } from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import { findLike2BuyV2StoreIds } from '@apis/like2buy-v2/like2BuyV2StoreApi';
import { getLike2BuyModerationStatus } from '@instagram-account/like2buy/like2buyUIUtils';
import {
  ILike2BuyModerationState,
  L2B_ITEM_REQUEST_LIMIT,
  Like2BuyModerationItemFilter,
  Like2BuyModerationStatus,
  NormalizedLike2BuyItems,
  RightsPermissionsByCuid,
  isLike2BuyModerationStatus,
} from '@instagram-account/redux/like2buy/like2buyModerationModel';
import {
  addLike2BuyItemsFailedToast,
  addLike2BuyItemsSucceededToast,
  deleteLike2BuyItemsFailedToast,
  deleteLike2BuyItemsSucceededToast,
  getLike2BuyPreviewFailedToast,
  hideLike2BuyItemsFailedToast,
  hideLike2BuyItemsSucceededToast,
  pinLike2BuyItemsFailedToast,
  pinLike2BuyItemsSucceededToast,
  publishLike2BuyItemFailedToast,
  publishLike2BuyItemSucceededToast,
  reorderLike2BuyItemFailedToast,
  unpinLike2BuyItemFailedToast,
  unpinLike2BuyItemSucceededToast,
  updateLike2BuyItemsFailedToast,
} from '@instagram-account/redux/like2buy/like2buyModerationToasts';
import { getOrderedLike2BuyItems } from '@instagram-account/redux/like2buy/selectors/getOrderedLike2BuyItems';
import { getConnectedInstagramBusinessAccountIdAsNetworkUserId } from '@instagram-account/redux/selectors/getInstagramBusinessAccountIdAsNetworkUserId';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import Cuid from '@models/Cuid';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefinedWithNetworkItem } from '@models/INetworkItem';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getClientId } from '@redux/selectors/getClientId';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import normalize, { Normalized } from '@utils/normalize';

const initialState: ILike2BuyModerationState = {
  items: Normalized.empty(),
  rightsPermissionsByCuid: PojoMap.empty(),
  itemFilter: 'allMedia',
  totalItemCount: 0,
  pinnedItemCuids: [],
  previewUrl: undefined,
  flags: {
    isInitialLike2BuyDataLoaded: false,
    isFetchingInitialItems: false,
    isFetchingNextItems: false,
    hasFetchedAllItems: false,
    isFetchingPreviewUrl: false,
    isFetchingPinnedItems: false,
    isFetchingLike2BuyStore: false,
  },
  errors: {},
};

const { actions, reducer } = createSlice({
  name: SliceNames.Like2BuyModeration,
  initialState,
  reducers: {
    setIsInitialLike2BuyDataLoaded(state) {
      state.flags.isInitialLike2BuyDataLoaded = true;
    },
    setItemFilter(state, action: PayloadAction<Like2BuyModerationItemFilter>) {
      state.itemFilter = action.payload;
    },
    requestInitialLike2BuyItems(state) {
      state.flags.isFetchingInitialItems = true;
      state.flags.hasFetchedAllItems = false;
      state.errors.fetchItemsError = undefined;
    },
    receiveInitialLike2BuyItemsSuceeded(
      state,
      action: PayloadAction<{
        items: NormalizedLike2BuyItems;
        totalItemCount: number;
        rightsPermissionsByCuid?: RightsPermissionsByCuid;
      }>,
    ) {
      state.flags.isFetchingInitialItems = false;
      state.items.byId = {
        ...state.items.byId,
        ...action.payload.items.byId,
      };
      state.items.orderedIds = action.payload.items.orderedIds;
      state.totalItemCount = action.payload.totalItemCount;

      state.rightsPermissionsByCuid =
        action.payload.rightsPermissionsByCuid ?? PojoMap.empty();

      if (action.payload.items.orderedIds.length < L2B_ITEM_REQUEST_LIMIT) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveInitialLike2BuyItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingInitialItems = false;
      state.errors.fetchItemsError = action.payload;
    },
    requestNextLike2BuyItems(state) {
      state.flags.isFetchingNextItems = true;
      state.errors.fetchItemsError = undefined;
    },
    receiveNextLike2BuyItemsSuceeded(
      state,
      action: PayloadAction<{
        items: NormalizedLike2BuyItems;
        rightsPermissionsByCuid?: RightsPermissionsByCuid;
      }>,
    ) {
      const { byId, orderedIds } = action.payload.items;

      state.items.byId = {
        ...state.items.byId,
        ...byId,
      };
      state.items.orderedIds = [...state.items.orderedIds, ...orderedIds];
      state.flags.isFetchingNextItems = false;

      state.rightsPermissionsByCuid = PojoMap.union(
        state.rightsPermissionsByCuid,
        action.payload.rightsPermissionsByCuid ?? PojoMap.empty(),
      );

      if (action.payload.items.orderedIds.length < L2B_ITEM_REQUEST_LIMIT) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveNextLike2BuyItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingNextItems = false;
      state.errors.fetchItemsError = action.payload;
    },
    receiveRightsPermissionsFailed(state, action: PayloadAction<string>) {
      state.errors.fetchRightsRequestStatusError = action.payload;
    },
    setHasFetchedAllItems(state) {
      state.flags.hasFetchedAllItems = true;
    },
    setPinnedItemCuids(state, action: PayloadAction<Cuid[]>) {
      state.pinnedItemCuids = action.payload;
    },
    requestPreviewUrl(state) {
      state.flags.isFetchingPreviewUrl = true;
      state.errors.fetchPreviewUrlError = undefined;
    },
    receivePreviewUrlSuceeded(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingPreviewUrl = false;
      state.previewUrl = action.payload;
    },
    receivePreviewUrlFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingPreviewUrl = false;
      state.errors.fetchPreviewUrlError = action.payload;
    },
    requestPinnedLike2BuyItems(state) {
      state.flags.isFetchingPinnedItems = true;
      state.errors.fetchPinnedItemsError = undefined;
    },
    receivePinnedLike2BuyItemsSuceeded(
      state,
      action: PayloadAction<IMlsMediaItem[]>,
    ) {
      state.flags.isFetchingPinnedItems = false;
      const pinnedItemsById = PojoMap.fromIndexing(
        action.payload,
        (item) => item.cuid,
      );

      state.items.byId = PojoMap.union(state.items.byId, pinnedItemsById);

      state.pinnedItemCuids = action.payload.map((item) => item.cuid);
    },
    receivePinnedLike2BuyItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingPinnedItems = false;
      state.errors.fetchPinnedItemsError = action.payload;
    },
    updateLike2BuyItems(state, action: PayloadAction<IMlsMediaItem[]>) {
      const updatedItemsById = PojoMap.fromIndexing(
        action.payload,
        (item) => item.cuid,
      );

      state.items.byId = PojoMap.union(state.items.byId, updatedItemsById);
    },
    removeLike2BuyItems(state, action: PayloadAction<Cuid[]>) {
      state.items = {
        ...state.items,
        ...Normalized.removeItems(state.items, action.payload),
      };
    },
    requestLike2BuyStore(state) {
      state.flags.isFetchingLike2BuyStore = true;
      state.errors.fetchStoreError = undefined;
    },
    receiveLike2BuyStoreSuceeded(
      state,
      action: PayloadAction<{ connectedStore?: ILike2BuyStoreData }>,
    ) {
      state.flags.isFetchingLike2BuyStore = false;
      state.connectedStore = action.payload.connectedStore;
    },
    receiveLike2BuyStoreFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingLike2BuyStore = false;
      state.errors.fetchStoreError = action.payload;
    },
  },
});

const getRightsPermissions = createThunk(
  (items: IMlsMediaItem[]) => async (dispatch) => {
    const partialCuids = items
      .filter(isDefinedWithNetworkItem)
      .map((item) => Cuid.toPartial(item.cuid));

    try {
      const itemsMetadata = await getItemsMetadata(partialCuids);

      const rightsPermissionsByCuid = itemsMetadata.reduce(
        (itemsByCuid, item) => {
          if (isDefined(item.rightsPermissions)) {
            return PojoMap.set(
              itemsByCuid,
              item.rootCuid,
              item.rightsPermissions,
            );
          }

          return itemsByCuid;
        },
        PojoMap.empty(),
      );

      return rightsPermissionsByCuid;
    } catch (_e) {
      dispatch(
        actions.receiveRightsPermissionsFailed(
          'An error occurred while fetching rights permissions. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

const fetchLike2BuyItems = createThunk(
  (offset: number = 0) =>
    async (dispatch, getState) => {
      const { itemFilter } = getState().instagram.like2buy;
      const status = isLike2BuyModerationStatus(itemFilter)
        ? itemFilter
        : undefined;

      const { items, totalCount } =
        await like2buyModerationApi.getLike2BuyItems({
          status,
          offset,
          limit: L2B_ITEM_REQUEST_LIMIT,
        });

      const rightsPermissions = await dispatch(getRightsPermissions(items));

      return {
        items: normalize(items, (item) => item.cuid),
        totalItemCount: totalCount,
        rightsPermissionsByCuid: rightsPermissions,
      };
    },
);

const updateLike2BuyItemsByCuid = createThunk(
  (cuids: Cuid[]) => async (dispatch) => {
    try {
      const updatedItems = await like2buyModerationApi.getLike2BuyItemsByCuid({
        cuids,
      });
      dispatch(actions.updateLike2BuyItems(updatedItems));
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          updateLike2BuyItemsFailedToast(cuids.length),
        ),
      );
    }
  },
);

export const fetchLike2BuyStore = createThunk(() => async (dispatch) => {
  dispatch(actions.requestLike2BuyStore());
  try {
    const connectedStore = await like2buyStoreApi.getLike2BuyStore();
    dispatch(actions.receiveLike2BuyStoreSuceeded({ connectedStore }));
  } catch (_e) {
    dispatch(
      actions.receiveLike2BuyStoreFailed(
        'An error occurred while fetching the Like2Buy store. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

export const loadInitialLike2BuyData = createThunk(() => async (dispatch) => {
  await Promise.all([
    dispatch(fetchLike2BuyStore()),
    dispatch(getInitialLike2BuyItems()),
    dispatch(getLike2BuyPreviewUrl()),
  ]);

  void dispatch(getPinnedLike2BuyItems());

  dispatch(actions.setIsInitialLike2BuyDataLoaded());
});

/**
 * Gets first set of Like2Buy items. Should be used on page load or after selecting a new filter.
 */
export const getInitialLike2BuyItems = createThunk(() => async (dispatch) => {
  dispatch(actions.requestInitialLike2BuyItems());

  try {
    const { items, rightsPermissionsByCuid, totalItemCount } =
      await dispatch(fetchLike2BuyItems());
    dispatch(
      actions.receiveInitialLike2BuyItemsSuceeded({
        items,
        totalItemCount,
        rightsPermissionsByCuid,
      }),
    );
  } catch (_e) {
    dispatch(
      actions.receiveLike2BuyStoreFailed(
        'An error occurred while fetching Like2Buy items. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

/**
 * Gets additional Like2Buy items. Should be used when user scrolls to the bottom of the item grid.
 */
export const getNextLike2BuyItems = createThunk(
  () => async (dispatch, getState) => {
    const currentItems = getOrderedLike2BuyItems(getState());
    const { hasFetchedAllItems, isFetchingNextItems } =
      getState().instagram.like2buy.flags;

    if (isFetchingNextItems || hasFetchedAllItems) {
      return;
    }

    dispatch(actions.requestNextLike2BuyItems());

    try {
      const { items, rightsPermissionsByCuid } = await dispatch(
        fetchLike2BuyItems(currentItems.length),
      );
      dispatch(
        actions.receiveNextLike2BuyItemsSuceeded({
          items,
          rightsPermissionsByCuid,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveNextLike2BuyItemsFailed(
          'An error occurred while fetching Like2Buy items. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

const getPinnedLike2BuyItems = createThunk(() => async (dispatch) => {
  dispatch(actions.requestPinnedLike2BuyItems());
  try {
    const pinnedItems = await like2buyModerationApi.getPinnedLike2BuyItems();
    dispatch(actions.receivePinnedLike2BuyItemsSuceeded(pinnedItems));
  } catch (_e) {
    dispatch(
      actions.receiveLike2BuyStoreFailed(
        'An error occurred while fetching pinned Like2Buy items. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

export const addLike2BuyItems = createThunk(
  (items: IContentLibraryItem[]) =>
    async (dispatch, getState): Promise<IMlsMediaItem[] | undefined> => {
      const clientId = getClientId(getState());
      const cuids = items.map((item) => Cuid.build(item, clientId));

      try {
        const { alreadyAddedItems } =
          await like2buyModerationApi.addLike2BuyItems({ cuids });
        const addedItemCount = cuids.length - alreadyAddedItems.length;
        if (addedItemCount > 0) {
          dispatch(
            toastNotificationsActions.addToastNotification(
              addLike2BuyItemsSucceededToast(addedItemCount),
            ),
          );
          void dispatch(getInitialLike2BuyItems());
        }

        // TODO PD-245819: Clean up dual write
        // Add like2buy items to like2buy v2
        const state = getState();
        const networkUserId =
          getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

        const storeIds = await findLike2BuyV2StoreIds({
          network: SocialNetwork.Instagram,
          networkUserId,
        });

        if (storeIds?.[0]) {
          const { alreadyAddedItems } = await addLike2BuyV2Items(storeIds[0], {
            cuids: cuids,
          });
          const alreadyAddedItemsCuids = alreadyAddedItems.map(
            (addedItem) => addedItem.item.cuid,
          );
          // if item is already in l2b v2 but archived then unarchive
          // this scenario can happen if you delete a l2b item, which will archive the l2b v2 item
          // and then add it back to l2b.
          if (alreadyAddedItemsCuids.length > 0) {
            const archivedItems = await getLike2BuyV2Items(storeIds[0], {
              limit: 10000,
              status: Like2BuyV2ModerationStatus.Archived,
            });

            const archivedItemsAddedCuid = archivedItems.mediaItems
              .map((mediaItem) => mediaItem.item.cuid)
              .filter((cuid) => alreadyAddedItemsCuids.includes(cuid));

            await addLike2BuyV2Items(storeIds[0], {
              cuids: archivedItemsAddedCuid,
              consistentWrite: true,
            });
          }
        }
        /* --------- */

        return alreadyAddedItems;
      } catch (e) {
        dispatch(
          toastNotificationsActions.addToastNotification(
            addLike2BuyItemsFailedToast,
          ),
        );
      }
    },
);

export const setItemFilterAndFetchItems = createThunk(
  (filter: Like2BuyModerationItemFilter) => (dispatch) => {
    dispatch(actions.setItemFilter(filter));
    void dispatch(getInitialLike2BuyItems());
  },
);

export const pinLike2BuyItem = createThunk((cuid: Cuid) => (dispatch) => {
  void dispatch(pinLike2BuyItems([cuid]));
});

export const pinLike2BuyItems = createThunk(
  (cuids: Cuid[]) => async (dispatch, getState) => {
    const { pinnedItemCuids } = getState().instagram.like2buy;
    const newPinnedItemCuids = [...pinnedItemCuids, ...cuids];

    try {
      await like2buyModerationApi.setPinnedLike2BuyItems({
        cuids: newPinnedItemCuids,
      });
      dispatch(actions.setPinnedItemCuids(newPinnedItemCuids));
      dispatch(
        toastNotificationsActions.addToastNotification(
          pinLike2BuyItemsSucceededToast(cuids.length),
        ),
      );
      void dispatch(updateLike2BuyItemsByCuid(cuids));

      // TODO PD-245819: Clean up dual write
      // pin items to like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        await setPinnedLike2BuyV2Items(storeIds[0], {
          cuids: newPinnedItemCuids,
          consistentWrite: true,
        });
      }
      /* --------- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          pinLike2BuyItemsFailedToast(cuids.length),
        ),
      );
    }
  },
);

export const unpinLike2BuyItem = createThunk(
  (cuid: Cuid) => async (dispatch, getState) => {
    const { pinnedItemCuids } = getState().instagram.like2buy;
    const cuidIndex = pinnedItemCuids.indexOf(cuid);
    if (cuidIndex < 0) {
      return;
    }

    const newPinnedItemCuids = [...pinnedItemCuids];
    newPinnedItemCuids.splice(cuidIndex, 1);

    try {
      await like2buyModerationApi.setPinnedLike2BuyItems({
        cuids: newPinnedItemCuids,
      });
      dispatch(actions.setPinnedItemCuids(newPinnedItemCuids));
      dispatch(
        toastNotificationsActions.addToastNotification(
          unpinLike2BuyItemSucceededToast,
        ),
      );
      void dispatch(updateLike2BuyItemsByCuid([cuid]));

      // TODO PD-245819: Clean up dual write
      // unpitems to like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        await setPinnedLike2BuyV2Items(storeIds[0], {
          cuids: newPinnedItemCuids,
          consistentWrite: true,
        });
      }
      /* --------- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          unpinLike2BuyItemFailedToast(newPinnedItemCuids.length),
        ),
      );
    }
  },
);

export const setPinnedLike2BuyItems = createThunk(
  (newCuids: Cuid[]) => async (dispatch, getState) => {
    const { pinnedItemCuids } = getState().instagram.like2buy;

    try {
      dispatch(actions.setPinnedItemCuids(newCuids));
      await like2buyModerationApi.setPinnedLike2BuyItems({ cuids: newCuids });

      // TODO PD-245819: Clean up dual write
      // set pinned to like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        await setPinnedLike2BuyV2Items(storeIds[0], {
          cuids: newCuids,
          consistentWrite: true,
        });
      }
      /* ----- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          reorderLike2BuyItemFailedToast,
        ),
      );
      dispatch(actions.setPinnedItemCuids(pinnedItemCuids));
    }
  },
);

export const publishLike2BuyItem = createThunk(
  (cuid: Cuid) => async (dispatch, getState) => {
    try {
      await like2buyModerationApi.publishLike2BuyItems({ cuids: [cuid] });
      dispatch(
        toastNotificationsActions.addToastNotification(
          publishLike2BuyItemSucceededToast,
        ),
      );
      void dispatch(updateLike2BuyItemsByCuid([cuid]));

      // TODO PD-245819: Clean up dual write
      // unarchive like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        const archivedItems = await getLike2BuyV2Items(storeIds[0], {
          limit: 10000,
          status: Like2BuyV2ModerationStatus.Archived,
        });

        const newUnarchivedItems = archivedItems.mediaItems
          .map((mediaItem) => mediaItem.item.cuid)
          .filter((currentCuid) => currentCuid === cuid);

        await addLike2BuyV2Items(storeIds[0], {
          cuids: newUnarchivedItems,
          consistentWrite: true,
        });
      }
      /* ----- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          publishLike2BuyItemFailedToast,
        ),
      );
    }
  },
);

const unpinHiddenOrDeletedItems = createThunk(
  (cuids: Cuid[]) => (dispatch, getState) => {
    const { pinnedItemCuids } = getState().instagram.like2buy;
    const updatedPinnedCuids = pinnedItemCuids.filter(
      (pinnedCuid) => !cuids.includes(pinnedCuid),
    );

    if (updatedPinnedCuids.length !== pinnedItemCuids.length) {
      dispatch(actions.setPinnedItemCuids(updatedPinnedCuids));
    }
  },
);

export const hideLike2BuyItems = createThunk(
  (cuids: Cuid[]) => async (dispatch, getState) => {
    try {
      await like2buyModerationApi.hideLike2BuyItems({ cuids });
      dispatch(
        toastNotificationsActions.addToastNotification(
          hideLike2BuyItemsSucceededToast(cuids.length),
        ),
      );
      void dispatch(updateLike2BuyItemsByCuid(cuids));
      dispatch(unpinHiddenOrDeletedItems(cuids));

      // TODO PD-245819: Clean up dual write
      // archive like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        const archivedItems = await getLike2BuyV2Items(storeIds[0], {
          limit: 10000,
          status: Like2BuyV2ModerationStatus.Archived,
        });

        const archivedItemsCuids = archivedItems.mediaItems.map(
          (mediaItem) => mediaItem.item.cuid,
        );

        const newArchivedItemsCuids = archivedItemsCuids.concat(cuids);

        await archiveLike2BuyV2Items(storeIds[0], {
          cuids: newArchivedItemsCuids,
          consistentWrite: true,
        });
      }
      /* ------- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          hideLike2BuyItemsFailedToast(cuids.length),
        ),
      );
    }
  },
);

export const deleteLike2BuyItems = createThunk(
  (cuids: Cuid[]) => async (dispatch, getState) => {
    try {
      await like2buyModerationApi.removeLike2BuyItems({ cuids });
      dispatch(
        toastNotificationsActions.addToastNotification(
          deleteLike2BuyItemsSucceededToast(cuids.length),
        ),
      );
      dispatch(actions.removeLike2BuyItems(cuids));
      dispatch(unpinHiddenOrDeletedItems(cuids));

      // TODO PD-245819: Clean up dual write
      // archive like2buy v2
      const state = getState();
      const networkUserId =
        getConnectedInstagramBusinessAccountIdAsNetworkUserId(state);

      const storeIds = await findLike2BuyV2StoreIds({
        network: SocialNetwork.Instagram,
        networkUserId,
      });

      if (storeIds?.[0]) {
        const archivedItems = await getLike2BuyV2Items(storeIds[0], {
          limit: 10000,
          status: Like2BuyV2ModerationStatus.Archived,
        });

        const archivedItemsCuids = archivedItems.mediaItems.map(
          (mediaItem) => mediaItem.item.cuid,
        );
        const newArchivedItemsCuids = archivedItemsCuids.concat(cuids);

        await archiveLike2BuyV2Items(storeIds[0], {
          cuids: newArchivedItemsCuids,
          consistentWrite: true,
        });
      }
      /* --------- */
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          deleteLike2BuyItemsFailedToast(cuids.length),
        ),
      );
    }
  },
);

export const getLike2BuyPreviewUrl = createThunk(() => async (dispatch) => {
  dispatch(actions.requestPreviewUrl());
  try {
    const { previewUrl } =
      await legacyLike2BuyApi.getLike2BuyCustomizationSettings();
    dispatch(actions.receivePreviewUrlSuceeded(previewUrl));
  } catch (e) {
    dispatch(
      actions.receivePreviewUrlFailed(
        'An error occurred while fetching the Like2Buy preview URL. Please try again or contact Bazaarvoice Support.',
      ),
    );

    dispatch(
      toastNotificationsActions.addToastNotification(
        getLike2BuyPreviewFailedToast,
      ),
    );
  }
});

/**
 * Handles updating Like2Buy item after UIA is closed.
 * If the item had the "Pinned" status and all products have been removed, remove the item from the pinned items and refetch the item.
 * If the item was in the "Needs Products" status and products have been added, publish the item.
 * Otherwise if the products have changed, refetch the item.
 */
export const handleUiaCloseResponse = createThunk(
  (response: IUiaModalClosedResponse) => (dispatch, getState) => {
    if (!response.productsChanged) {
      return;
    }

    const { items } = getState().instagram.like2buy;
    const clientId = getClientId(getState());
    const cuid = Cuid.fromPartial(
      PartialCuid(response.universalItemId),
      clientId,
    );
    const { pinnedItemCuids } = getState().instagram.like2buy;

    const like2buyItem = Normalized.getById(items, cuid);

    if (like2buyItem) {
      const moderationStatus = getLike2BuyModerationStatus(like2buyItem);
      const productsLength = response.products.length;
      if (
        moderationStatus === Like2BuyModerationStatus.Pinned &&
        productsLength === 0
      ) {
        const updatedPinnedCuids = pinnedItemCuids.filter(
          (pinnedCuid) => pinnedCuid !== cuid,
        );
        dispatch(actions.setPinnedItemCuids(updatedPinnedCuids));
        void dispatch(updateLike2BuyItemsByCuid([cuid]));
      } else if (
        moderationStatus === Like2BuyModerationStatus.NeedsProducts &&
        productsLength > 0
      ) {
        void dispatch(publishLike2BuyItem(cuid));
      } else {
        void dispatch(updateLike2BuyItemsByCuid([cuid]));
      }
    }
  },
);

export default reducer;
